<script lang="ts">
const __DsBlock = {
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_number.name',
	description: '$t:blocks.input_number.description',
	type: 'input',
	category: 'input',
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => {
			if (isPrimitiveTypeDecimal(primitiveType)) return [];

			const types = ['number'];
			if (isPrimitiveTypeInteger(primitiveType)) types.push('identifier');
			return types;
		},
		recommendedDisplays: ['$builtin:DisplayFormattedValue'],
	},
	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'min',
			label: '$t:blocks.common.props.min',
		},
		{
			name: 'max',
			label: '$t:blocks.common.props.max',
		},
		{
			name: 'step',
			label: '$t:blocks.common.props.step',
		},
		{
			name: 'stepSnapping',
			label: '$t:blocks.common.props.step_snapping',
		},
		{
			name: 'placeholder',
			label: '$t:blocks.common.props.placeholder',
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'block',
			label: '$t:blocks.common.props.block',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
	],
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { InputNumberProps } from '#layers/ui/public';
import { reactivePick } from '@vueuse/core';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { isPrimitiveTypeDecimal, isPrimitiveTypeInteger } from '#layers/data-model/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends
	InputBlockProps,
	Pick<InputNumberProps, 'placeholder' | 'variant' | 'size' | 'min' | 'max' | 'step' | 'stepSnapping'> {
	/**
	 * Render the input full width
	 */
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), { block: true });
const model = defineModel<number>();

;

const forwarded = reactivePick(props, 'placeholder', 'variant', 'size', 'min', 'max', 'step', 'stepSnapping', 'readonly');
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInputNumber
			v-bind="forwarded"
			v-model="modelValue"
			:disabled
			:aria-readonly="readonly"
			class="min-w-0"
			:class="{ 'w-full flex-1': block }"
		/>
	</DTooltip>
</template>
